import React, { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../components/atoms';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { DataTable } from '../../components/organisms';
import { useAlbumActions, useAlbums } from '../../hooks/useAlbums';
import type { Album, CreateAlbumRequest, UpdateAlbumRequest } from '../../types/api/album';
import { formatAlbumDateForDisplay } from '../../types/api/album';
import type { Column, TableAction } from '../../types/table';
import { resolveAssetUrl } from '../../utils/url';
import { AlbumFormModal } from './components/AlbumFormModal';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

export const AlbumManagementPage: React.FC = () => {
  const { fetchAlbums } = useAlbums();
  const { createAlbum, updateAlbum, getAlbum, deleteAlbum } = useAlbumActions();

  const refreshKeyRef = useRef(0);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedAlbum(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    async (album: Album) => {
      try {
        const fullAlbum = await getAlbum(album.albumId);
        setSelectedAlbum(fullAlbum);
        setIsEditModalOpen(true);
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to load album details'));
      }
    },
    [getAlbum]
  );

  const handleDelete = useCallback((album: Album) => {
    setSelectedAlbum(album);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCreateSubmit = useCallback(
    async (
      data: CreateAlbumRequest | UpdateAlbumRequest,
      coverImageFile?: File | null,
      imageFile?: File | null
    ) => {
      try {
        await createAlbum(data as CreateAlbumRequest, coverImageFile || undefined, imageFile || undefined);
        toast.success('Album created successfully');
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to create album'));
      }
    },
    [createAlbum, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (
      data: CreateAlbumRequest | UpdateAlbumRequest,
      coverImageFile?: File | null,
      imageFile?: File | null
    ) => {
      if (!selectedAlbum) return;
      try {
        await updateAlbum(
          selectedAlbum.albumId,
          data as UpdateAlbumRequest,
          coverImageFile || undefined,
          imageFile || undefined
        );
        toast.success('Album updated successfully');
        setIsEditModalOpen(false);
        setSelectedAlbum(null);
        handleRefresh();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to update album'));
      }
    },
    [selectedAlbum, updateAlbum, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedAlbum) return;
    try {
      await deleteAlbum(selectedAlbum.albumId);
      toast.success('Album deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedAlbum(null);
      handleRefresh();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to delete album'));
    }
  }, [selectedAlbum, deleteAlbum, handleRefresh]);

  const columns = useMemo<Column<Album>[]>(
    () => [
      {
        id: 'album',
        header: 'Album',
        accessor: 'name',
        minWidth: '260px',
        cell: (row) => (
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0 border">
              {row.coverArtUrl ? (
                <img
                  src={resolveAssetUrl(row.coverArtUrl)}
                  alt={row.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-xs text-gray-500">No cover</span>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="text-sm font-semibold text-gray-900 truncate">{row.name}</p>
              <p className="text-xs text-gray-500 truncate">
                {row.musicVideoCount ?? 0} linked music videos
              </p>
            </div>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        accessor: 'description',
        cell: (row) => (
          <p className="text-sm text-gray-700 line-clamp-2 max-w-xs">{row.description || 'N/A'}</p>
        ),
        hideOnMobile: true,
      },
      {
        id: 'releaseDate',
        header: 'Release Date',
        accessor: 'releaseDate',
        cell: (row) => (
          <span className="text-sm text-gray-600">{formatAlbumDateForDisplay(row.releaseDate)}</span>
        ),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessor: 'updatedAt',
        cell: (row) => (
          <span className="text-sm text-gray-600">{formatAlbumDateForDisplay(row.updatedAt)}</span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const actions = useMemo<TableAction<Album>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleDelete]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Album Management</h1>
          <p className="text-gray-600 mt-1 pl-3">Manage album metadata and cover images</p>
        </div>
        <Button
          onClick={handleCreate}
          leftIcon={
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
          }
        >
          Add Album
        </Button>
      </div>

      <DataTable<Album>
        columns={columns}
        fetchData={fetchAlbums}
        searchable
        searchPlaceholder="Search albums by name..."
        initialPageSize={10}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Album management table"
        key={refreshKeyRef.current}
      />

      <AlbumFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        mode="create"
      />

      <AlbumFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedAlbum(null);
        }}
        onSubmit={handleEditSubmit}
        mode="edit"
        album={selectedAlbum || undefined}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedAlbum(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Album"
        message={
          selectedAlbum
            ? `Are you sure you want to delete "${selectedAlbum.name}"? This action cannot be undone.`
            : 'Are you sure you want to delete this album?'
        }
      />
    </div>
  );
};

export default AlbumManagementPage;
